<script setup lang="ts">
import { computed } from 'vue'

import type { ReportColumn } from '../../models/column.types'
import type {
  FilterDropdownable,
  FilterDropdownableValue,
  ReportFilter,
} from '../../models/filter.types'
import {
  isNumericReportColumn,
  isPercentageReportColumn,
  isReportFilterDropdownableColumn,
} from '../../service/reportColumnKind'
import { getReportFilterModeDefinition } from '../../service/reportFilterModes'

import { useI18n } from '@/shared/i18n'
import { Input, MultiSelect, Select, type SelectOption } from '@/shared/ui'
import type { UiAsyncState } from '@/shared/ui/types'

const model = defineModel<ReportFilter>({ required: true })
const props = withDefaults(
  defineProps<{
    column: ReportColumn
    dropdownable?: FilterDropdownable | undefined
    dropdownEnabled?: boolean
    state?: UiAsyncState
  }>(),
  { dropdownEnabled: false, state: 'ready' },
)
const { t } = useI18n()
const valueShape = computed(() => getReportFilterModeDefinition(model.value.match_mode).valueShape)
const numeric = computed(() => isNumericReportColumn(props.column))
const percentage = computed(
  () =>
    model.value.is_change === true ||
    isPercentageReportColumn(props.column) ||
    model.value.sub_type === 'percentage',
)
const dropdownOptions = computed<SelectOption<FilterDropdownableValue>[]>(() => {
  const values = [...(props.dropdownable?.values ?? [])]
  const selected = Array.isArray(model.value.value) ? model.value.value : [model.value.value]
  for (const value of selected) {
    if (isNonBlankDropdownValue(value) && !values.includes(value)) {
      values.push(value)
    }
  }
  return values.map((value) => ({ value, label: String(value) }))
})
const dropdownCandidate = computed(
  () =>
    ['single', 'multiple'].includes(valueShape.value) &&
    ['equals', 'not_equals', 'in', 'not_in'].includes(model.value.match_mode) &&
    isReportFilterDropdownableColumn(props.column) &&
    ((props.dropdownable?.values?.length ?? 0) > 0 ||
      (props.dropdownEnabled && props.state !== 'ready') ||
      (props.dropdownEnabled && Boolean(props.dropdownable?.unique_count))),
)
const usesMultipleDropdown = computed(
  () => dropdownCandidate.value && valueShape.value === 'multiple',
)
const usesSingleDropdown = computed(() => dropdownCandidate.value && valueShape.value === 'single')
const selectedValues = computed<FilterDropdownableValue[]>({
  get: () => {
    const value = model.value.value
    const values = Array.isArray(value)
      ? value
      : value === null || value === undefined
        ? []
        : [value]
    return values.filter(
      (item): item is FilterDropdownableValue =>
        typeof item === 'string' || typeof item === 'number',
    )
  },
  set: (value) => updateValue([...value]),
})
const selectedValue = computed<FilterDropdownableValue | null>({
  get: () => {
    const value = model.value.value
    return isNonBlankDropdownValue(value) ? value : null
  },
  set: (value) => updateValue(value),
})
const listValue = computed<string>({
  get: () => {
    const value = model.value.value
    if (Array.isArray(value)) return value.map(String).join(', ')
    return value === null || value === undefined ? '' : String(value)
  },
  set: updateValue,
})
const rangeStart = computed<string | number>({
  get: () => rangeValue(0),
  set: (value) => updateRange(0, value),
})
const rangeEnd = computed<string | number>({
  get: () => rangeValue(1),
  set: (value) => updateRange(1, value),
})
const scalarValue = computed<string | number>({
  get: () => {
    const value = model.value.value
    return typeof value === 'string' || typeof value === 'number' ? value : ''
  },
  set: updateValue,
})

function rangeValue(index: number): string | number {
  const range = Array.isArray(model.value.value)
    ? model.value.value
    : String(model.value.value ?? '').split(',')
  const value = range[index]
  return typeof value === 'string' || typeof value === 'number' ? value : ''
}

function isNonBlankDropdownValue(value: unknown): value is FilterDropdownableValue {
  return typeof value === 'number' || (typeof value === 'string' && value.trim().length > 0)
}

function updateRange(index: number, value: string | number): void {
  const range: [string | number, string | number] = [rangeValue(0), rangeValue(1)]
  range[index] = value
  updateValue(range)
}

function updateValue(value: unknown): void {
  model.value = { ...model.value, value }
}
</script>

<template>
  <div v-if="valueShape === 'none'" class="filter-value-editor__empty w-0" />
  <MultiSelect
    v-else-if="usesMultipleDropdown"
    v-model="selectedValues"
    :options="dropdownOptions"
    :label="t('reports.filters.value')"
    :placeholder="t('reports.filters.valuePlaceholder')"
    :empty-label="t('reports.filters.noOptions')"
    :state="state"
    searchable
    size="sm"
  />
  <Select
    v-else-if="usesSingleDropdown"
    v-model="selectedValue"
    :options="dropdownOptions"
    :label="t('reports.filters.value')"
    :placeholder="t('reports.filters.valuePlaceholder')"
    :state="state"
    searchable
    size="sm"
  />
  <Input
    v-else-if="valueShape === 'multiple'"
    v-model="listValue"
    type="text"
    :label="t('reports.filters.value')"
    :placeholder="t('reports.filters.valuePlaceholder')"
    size="sm"
  />
  <div
    v-else-if="valueShape === 'range'"
    class="filter-value-editor__range grid min-w-64 grid-cols-[repeat(2,_minmax(7rem,_1fr))] gap-sm"
  >
    <Input
      v-model="rangeStart"
      :type="numeric ? 'number' : 'text'"
      v-bind="numeric ? { step: 'any' } : {}"
      :label="t('reports.filters.from')"
      :placeholder="t('reports.filters.from')"
      size="sm"
    />
    <Input
      v-model="rangeEnd"
      :type="numeric ? 'number' : 'text'"
      v-bind="numeric ? { step: 'any' } : {}"
      :label="t('reports.filters.to')"
      :placeholder="t('reports.filters.to')"
      size="sm"
    />
  </div>
  <Input
    v-else
    v-model="scalarValue"
    :type="numeric ? 'number' : 'text'"
    v-bind="numeric ? { step: 'any' } : {}"
    :label="t('reports.filters.value')"
    :placeholder="t('reports.filters.valuePlaceholder')"
    size="sm"
  >
    <template v-if="percentage" #end>{{ t('reports.filters.percentSign') }}</template>
  </Input>
</template>
